import "./App.scss";
import React, { useState } from "react";
import {
  LayoutDashboard,
  Kanban,
  Calendar,
  MessageSquare,
  Zap,
  MapPin,
  Clock,
  RefreshCw,
  ShieldCheck,
  UserCheck,
  ChevronRight,
} from "lucide-react";

const App = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState("14:20 GMT");

  const handleSync = () => {
    setIsSyncing(true);
    setTimeout(() => {
      const now = new Date();
      setLastSynced(
        `${now.getHours()}:${now.getMinutes().toString().padStart(2, "0")} GMT`,
      );
      setIsSyncing(false);
    }, 1500);
  };

  const tasks = [
    {
      id: "TASK-882",
      title: "Q2 Retail Banner Deployment",
      status: "In Progress",
      priority: "Critical",
    },
    {
      id: "TASK-894",
      title: "SEO Metadata Audit - Manchester",
      status: "Done",
      priority: "High",
    },
    {
      id: "TASK-901",
      title: "Flash Sale Toggle Logic",
      status: "In Progress",
      priority: "High",
    },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-blue-100">
      {/* 1. SIDEBAR */}
      <aside className="hidden lg:flex flex-col w-72 bg-white border-r border-slate-200 p-8 sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-black text-xl">
            ICG
          </div>
          <span className="font-black text-xl tracking-tight">PORTAL</span>
        </div>

        <nav className="space-y-2 flex-1">
          {[
            { name: "Performance", icon: LayoutDashboard, active: false },
            { name: "Task Board", icon: Kanban, active: true },
            { name: "Schedule", icon: Calendar, active: false },
            { name: "Comms", icon: MessageSquare, active: false },
          ].map((item) => (
            <button
              key={item.name}
              className={`w-full flex items-center gap-4 px-4 py-3 rounded-2xl transition-all ${item.active ? "bg-slate-950 text-white shadow-lg" : "text-slate-500 hover:bg-slate-100"}`}
            >
              <item.icon size={20} />
              <span className="font-semibold text-sm">{item.name}</span>
            </button>
          ))}
        </nav>

        <div className="mt-auto bg-blue-50 p-6 rounded-[2rem] border border-blue-100">
          <div className="flex items-center gap-2 mb-2 text-blue-600">
            <Zap size={16} fill="currentColor" />
            <span className="text-[10px] font-black uppercase tracking-widest">
              Strategic Link
            </span>
          </div>
          <p className="text-xs text-blue-900 font-medium leading-relaxed">
            Studio-to-Site connection is currently{" "}
            <span className="underline decoration-2">Optimized</span>.
          </p>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 p-4 lg:p-10 max-w-7xl mx-auto w-full">
        {/* 2. HEADER */}
        <header className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
          <div>
            <div className="flex items-center gap-3 text-slate-400 mb-1">
              <span className="font-bold text-slate-950">ICG DIGITAL</span>
              <span className="text-xs">×</span>
              <span className="font-medium">ACME Co.</span>
            </div>
            <h1 className="text-3xl font-black tracking-tight italic">
              Manchester Retail Digital Operations
            </h1>
          </div>

          <div className="bg-emerald-50 border border-emerald-100 px-6 py-3 rounded-full flex items-center gap-3">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <div className="text-sm">
              <span className="text-slate-500 font-medium italic">
                Lead Strategist Status:
              </span>
              <span className="text-emerald-700 font-bold ml-2">
                Active (Mon-Thu)
              </span>
            </div>
          </div>
        </header>

        {/* 3. WEEKEND READINESS HERO */}
        <section className="bg-slate-950 rounded-[2.5rem] p-8 lg:p-12 text-white mb-10 shadow-2xl relative overflow-hidden">
          <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-black italic mb-6 tracking-tighter">
                Campaign Continuity Brief _
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8 max-w-md">
                Transitioning to autonomous monitoring for the weekend retail
                peak. The Preston Studio remains on remote oversight.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/5 border border-white/10 p-5 rounded-3xl flex-1 min-w-[200px]">
                  <MapPin className="text-blue-400 mb-3" />
                  <div className="text-[10px] font-black uppercase text-slate-500 mb-1">
                    Site Presence
                  </div>
                  <div className="font-bold">London Team On-Site</div>
                </div>
                <div className="bg-white/5 border border-white/10 p-5 rounded-3xl flex-1 min-w-[200px]">
                  <ShieldCheck className="text-emerald-400 mb-3" />
                  <div className="text-[10px] font-black uppercase text-slate-500 mb-1">
                    Contact Protocol
                  </div>
                  <div className="font-bold">L2 Emergency Active</div>
                </div>
              </div>
            </div>
            <div className="hidden lg:block bg-gradient-to-br from-blue-600 to-indigo-900 h-64 rounded-[2rem] shadow-inner opacity-50 border border-white/20"></div>
          </div>
        </section>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* 4. TASK BOARD SYNC */}
          <section className="lg:col-span-2 bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-xl font-black italic">Task Board Sync</h3>
                <code className="text-[10px] font-mono bg-slate-100 text-slate-500 px-2 py-1 rounded mt-2 inline-block tracking-widest">
                  BOARD_ID: TR-MAN-2026
                </code>
              </div>
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-bold transition-all disabled:opacity-50"
              >
                <RefreshCw
                  size={18}
                  className={isSyncing ? "animate-spin" : ""}
                />
                {isSyncing ? "Syncing..." : "Sync Board"}
              </button>
            </div>

            <div className="space-y-4">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className="group flex items-center justify-between p-5 rounded-3xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all cursor-default"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-2 h-2 rounded-full ${task.status === "Done" ? "bg-emerald-500" : "bg-blue-500 animate-pulse"}`}
                    />
                    <div>
                      <div className="text-sm font-black text-slate-800">
                        {task.title}
                      </div>
                      <code className="text-[10px] font-mono text-slate-400 uppercase tracking-tighter">
                        LOG ID: {task.id}
                      </code>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest ${task.priority === "Critical" ? "bg-rose-100 text-rose-600" : "bg-orange-100 text-orange-600"}`}
                    >
                      {task.priority}
                    </span>
                    <ChevronRight
                      size={16}
                      className="text-slate-300 group-hover:text-blue-400 transition-colors"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center text-[10px] font-mono text-slate-400">
              LAST GLOBAL SYNC: {lastSynced}
            </div>
          </section>

          <div className="space-y-8">
            {/* 5. CORE DIGIT TEAM SECTION */}
            <section className="bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-black italic mb-6">Digit Team Hub</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
                    <UserCheck size={20} />
                  </div>
                  <div>
                    <div className="text-xs font-black text-blue-600 uppercase tracking-widest mb-1">
                      Preston Studio
                    </div>
                    <div className="font-bold text-sm">Chief Strategist</div>
                    <div className="text-xs text-slate-500 italic">
                      Remote Oversight
                    </div>
                  </div>
                </div>
                <div className="h-px bg-slate-100 w-full" />
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-slate-900 flex items-center justify-center text-white">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <div className="text-xs font-black text-blue-600 uppercase tracking-widest mb-1">
                      London Site
                    </div>
                    <div className="font-bold text-sm">Implementation Lead</div>
                    <div className="text-xs text-slate-500 italic">
                      On-Site Execution
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 6. SYSTEM HEALTH */}
            <section className="bg-slate-950 rounded-[2.5rem] p-8 text-white shadow-xl">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-white/10 rounded-2xl text-emerald-400">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    System Health
                  </div>
                  <div className="text-2xl font-black italic leading-none">
                    99.9% Uptime
                  </div>
                </div>
              </div>
              <div className="text-[10px] font-mono text-emerald-400/60 uppercase">
                Nodes: Preston-01 // MCR-Retail-04 // LDN-Sync
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default App;